/**
 * Shell: step 1 category pick → step 2 configure.
 * residential_garage opens the original sectional ConfiguratorLayout (untouched files).
 */

import { useState } from "react";
import type {
  AccessCategoryId,
  AccessConfiguratorOptions,
  AccessRogerSeriesRule,
} from "@shared/access-configurator";
import {
  resolveAccessColors,
  resolveAccessPhotoCatalog,
  resolveAccessRogerRules,
  resolveEnabledCategories,
} from "@shared/access-configurator";
import ConfiguratorLayout from "@/components/price-generator/ConfiguratorLayout";
import type {
  ConfiguratorFormValues,
  ConfiguratorProductOption,
  PriceCombination,
} from "@/components/price-generator/ConfiguratorForm";
import CategoryPicker from "./CategoryPicker";
import AccessBackBar from "./AccessBackBar";
import AccessConfiguratorLayout from "./AccessConfiguratorLayout";
import type { AccessFormValues } from "./AccessForm";

interface AccessConfiguratorShellProps {
  options?: AccessConfiguratorOptions | null;
  rogerRules?: AccessRogerSeriesRule[] | null;
  token?: string;
  embedMode?: boolean;
  /** Sectional (original) product options — used when residential_garage is selected */
  sectionalProductOptions?: ConfiguratorProductOption[];
  sectionalPriceCombinations?: PriceCombination[];
  sectionalMotivOrder?: string[];
  onSubmit: (values: AccessFormValues) => Promise<void>;
  onSubmitSectional?: (values: ConfiguratorFormValues, token: string) => Promise<void>;
  submitting?: boolean;
}

export default function AccessConfiguratorShell({
  options,
  rogerRules,
  token,
  embedMode = false,
  sectionalProductOptions = [],
  sectionalPriceCombinations = [],
  sectionalMotivOrder,
  onSubmit,
  onSubmitSectional,
  submitting,
}: AccessConfiguratorShellProps) {
  const [categoryId, setCategoryId] = useState<AccessCategoryId | null>(null);
  const categories = resolveEnabledCategories(options);
  const photoCatalog = resolveAccessPhotoCatalog(options);
  const colors = resolveAccessColors(options, categoryId ?? undefined);
  const rules = resolveAccessRogerRules(rogerRules);

  if (!categoryId) {
    return <CategoryPicker categories={categories} onSelect={setCategoryId} />;
  }

  const shellClass = embedMode
    ? "flex flex-col flex-1 min-h-0 w-full"
    : "flex flex-col min-h-[100dvh] w-full";

  return (
    <div className={shellClass}>
      <div className="shrink-0 border-b border-border bg-background px-6 py-3">
        <AccessBackBar categoryId={categoryId} onBack={() => setCategoryId(null)} />
      </div>
      <div className="flex-1 min-h-0 flex flex-col">
        {categoryId === "residential_garage" ? (
          <ConfiguratorLayout
            configuratorProductOptions={sectionalProductOptions}
            priceCombinations={sectionalPriceCombinations}
            configuratorMotivOrder={sectionalMotivOrder}
            embedMode={embedMode}
            embedToken={token}
            onSubmitToPublic={
              onSubmitSectional && token
                ? (values, submitToken) => onSubmitSectional(values, submitToken)
                : undefined
            }
          />
        ) : (
          <AccessConfiguratorLayout
            categoryId={categoryId}
            colors={colors}
            photoCatalog={photoCatalog}
            rogerRules={rules}
            token={token}
            embedMode={embedMode}
            onSubmit={onSubmit}
            submitting={submitting}
          />
        )}
      </div>
    </div>
  );
}
